import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Read a deep-link parameter exactly once and then remove it from the URL.
 *
 * A dashboard action links to a screen with a panel already open, for example
 * `/inventory?action=receive`. The panel is opened through the normal page state
 * rather than by URL, so the parameter is consumed and dropped: reloading the
 * page, or coming back to it, does not spring the form open again.
 *
 * The latest handler is kept in a ref so callers can pass an inline closure
 * without making the effect re-run on every render.
 */
export function useDeepLink(name: string, onValue: (value: string) => void): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(name);
  const handler = useRef(onValue);
  handler.current = onValue;

  useEffect(() => {
    if (value === null) return;
    handler.current(value);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(name);
        return next;
      },
      { replace: true },
    );
  }, [value, name, setSearchParams]);
}
